import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { downloadBlob } from "../nativeDownload";

export default function MultiArchive() {
  const [multiEvents, setMultiEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { loadMultiEvents(); }, []);

  const loadMultiEvents = async () => {
    try {
      const res = await api.get("/archive/multiday");
      setMultiEvents(res.data);
    } catch(e) { setError("Failed to load"); }
    finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    try {
      setDetail(null);
      setSelected(id);
      const res = await api.get("/archive/multiday/" + id);
      setDetail(res.data);
    } catch(e) { setError("Failed to load details"); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this multi-day competition?")) return;
    try {
      await api.delete("/archive/multiday/" + id);
      setMultiEvents(prev => prev.filter(e => e.id !== id));
      if (selected === id) { setSelected(null); setDetail(null); }
    } catch(e) { setError("Failed to delete"); }
  };

  const isMedal = detail?.days?.[0]?.game_type?.includes("Medal") || detail?.days?.[0]?.game_type?.includes("Stroke");

  const exportToExcel = () => {
    if (!detail) return;
    const ptsLabel = isMedal ? "Net" : "Pts";
    const aoa = [];

    aoa.push([detail.event.name]);
    if (detail.event.description) aoa.push([detail.event.description]);
    aoa.push([]);

    // Section 1 & 2: one self-contained table per day, each with its own results and prizes
    detail.days.forEach(d => {
      aoa.push([`Day ${d.day_number}`]);
      aoa.push([
        d.date ? new Date(d.date).toLocaleDateString('en-GB') : '',
        "Venue:", d.venue || '',
        "Course:", d.course_name || '',
        "Format:", d.game_type || ''
      ]);
      aoa.push([]);
      aoa.push(["Rank", "Player", "HCP", "Gross", ptsLabel]);

      const dayPlayers = detail.results
        .filter(p => p.days[d.day_number])
        .sort((a, b) => (a.days[d.day_number].rank || 999) - (b.days[d.day_number].rank || 999));
      dayPlayers.forEach(p => {
        const dp = p.days[d.day_number];
        aoa.push([dp.rank, p.player_name, p.handicap, dp.gross, isMedal ? dp.net : dp.points]);
      });
      aoa.push([]);

      if (d.nearest_pin || d.nearest_pin2 || d.longest_drive) {
        aoa.push([`Day ${d.day_number} Prizes`]);
        if (d.nearest_pin) aoa.push(["Nearest Pin:", d.nearest_pin]);
        if (d.nearest_pin2) aoa.push(["Nearest Pin 2:", d.nearest_pin2]);
        if (d.longest_drive) aoa.push(["Longest Drive:", d.longest_drive]);
      }
      aoa.push([]);
      aoa.push([]);
    });

    // Section 3: combined score over all days
    aoa.push([`Combined — ${detail.days.length} Day Total`]);
    aoa.push([]);
    aoa.push(["Rank", "Player", "HCP", `Total ${ptsLabel}`, "Total Gross", "Days Played"]);
    detail.results.forEach(p => {
      aoa.push([p.overall_rank, p.player_name, p.handicap, isMedal ? p.total_net : p.total_points, p.total_gross, p.days_played]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, "Multi Day Results");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), detail.event.name.replace(/\s+/g, '_') + ".xlsx");
  };

  const exportToPDF = () => {
    if (!detail) return;
    const ptsLabel = isMedal ? "Net" : "Pts";
    const doc = new jsPDF();
    let y = 20;

    const ensureRoom = (needed) => { if (y + needed > 280) { doc.addPage(); y = 20; } };

    doc.setFontSize(16);
    doc.text(detail.event.name, 14, y);
    y += 8;
    if (detail.event.description) {
      doc.setFontSize(10);
      doc.text(detail.event.description, 14, y);
      y += 8;
    }

    // Section 1 & 2: one self-contained table per day, each with its own results and prizes
    detail.days.forEach(d => {
      ensureRoom(30);
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text(`Day ${d.day_number}`, 14, y);
      doc.setFont(undefined, 'normal');
      y += 6;
      doc.setFontSize(10);
      doc.text(`${d.date ? new Date(d.date).toLocaleDateString('en-GB') : ''}   Venue: ${d.venue || '-'}   Course: ${d.course_name || '-'}   Format: ${d.game_type || '-'}`, 14, y);
      y += 8;

      doc.setFontSize(10);
      const posX = 14, playerX = 32, hcpX = 130, grossX = 155, ptsX = 178;
      doc.setFont(undefined, 'bold');
      doc.text("Rank", posX, y);
      doc.text("Player", playerX, y);
      doc.text("HCP", hcpX, y);
      doc.text("Gross", grossX, y);
      doc.text(ptsLabel, ptsX, y);
      doc.setFont(undefined, 'normal');
      y += 5;
      doc.line(14, y, 196, y);
      y += 6;

      const dayPlayers = detail.results
        .filter(p => p.days[d.day_number])
        .sort((a, b) => (a.days[d.day_number].rank || 999) - (b.days[d.day_number].rank || 999));
      dayPlayers.forEach(p => {
        ensureRoom(7);
        const dp = p.days[d.day_number];
        doc.text(String(dp.rank ?? '-'), posX, y);
        doc.text(p.player_name || '', playerX, y);
        doc.text(String(p.handicap ?? '-'), hcpX, y);
        doc.text(String(dp.gross ?? '-'), grossX, y);
        doc.text(String(isMedal ? dp.net : dp.points), ptsX, y);
        y += 6;
      });
      y += 4;

      if (d.nearest_pin || d.nearest_pin2 || d.longest_drive) {
        ensureRoom(24);
        doc.setFont(undefined, 'bold');
        doc.text(`Day ${d.day_number} Prizes`, 14, y);
        doc.setFont(undefined, 'normal');
        y += 6;
        if (d.nearest_pin) { doc.text(`Nearest Pin: ${d.nearest_pin}`, 14, y); y += 6; }
        if (d.nearest_pin2) { doc.text(`Nearest Pin 2: ${d.nearest_pin2}`, 14, y); y += 6; }
        if (d.longest_drive) { doc.text(`Longest Drive: ${d.longest_drive}`, 14, y); y += 6; }
      }
      y += 8;
    });

    // Section 3: combined score over all days
    ensureRoom(30);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text(`Combined — ${detail.days.length} Day Total`, 14, y);
    doc.setFont(undefined, 'normal');
    y += 8;

    doc.setFontSize(10);
    const posX2 = 14, playerX2 = 32, hcpX2 = 110, ptsX2 = 140, grossX2 = 165, daysX2 = 188;
    doc.setFont(undefined, 'bold');
    doc.text("Rank", posX2, y);
    doc.text("Player", playerX2, y);
    doc.text("HCP", hcpX2, y);
    doc.text(`Total ${ptsLabel}`, ptsX2, y);
    doc.text("Gross", grossX2, y);
    doc.text("Days", daysX2, y);
    doc.setFont(undefined, 'normal');
    y += 5;
    doc.line(14, y, 196, y);
    y += 6;

    detail.results.forEach(p => {
      ensureRoom(7);
      doc.text(String(p.overall_rank ?? '-'), posX2, y);
      doc.text(p.player_name || '', playerX2, y);
      doc.text(String(p.handicap ?? '-'), hcpX2, y);
      doc.text(String(isMedal ? p.total_net : p.total_points), ptsX2, y);
      doc.text(String(p.total_gross ?? '-'), grossX2, y);
      doc.text(String(p.days_played ?? '-'), daysX2, y);
      y += 6;
    });

    downloadBlob(doc.output("blob"), detail.event.name.replace(/\s+/g, '_') + ".pdf");
  };

  return (
    <Layout>
      <div className="flex h-full">
        {/* Left panel - list */}
        <div style={{ width: "280px", borderRight: "1px solid #e5e7eb", overflowY: "auto", flexShrink: 0 }}>
          <div className="bg-green-800 text-white px-4 py-2 font-semibold text-sm">Multi-Day Competitions</div>
          {loading && <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>}
          {error && <div className="px-4 py-2 text-sm text-red-600">{error}</div>}
          {!loading && multiEvents.length === 0 && (
            <div className="px-4 py-4 text-sm text-gray-400">No multi-day competitions yet. Use the Results page to create one.</div>
          )}
          {multiEvents.map(ev => (
            <div key={ev.id}
              onClick={() => loadDetail(ev.id)}
              className={"px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-green-50 " + (selected === ev.id ? "bg-green-100" : "")}>
              <div className="font-semibold text-sm text-green-800">{ev.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{ev.day_count} days · {new Date(ev.created_at).toLocaleDateString('en-GB')}</div>
              {ev.description && <div className="text-xs text-gray-400 mt-0.5 italic">{ev.description}</div>}
              <button onClick={e => { e.stopPropagation(); deleteEvent(ev.id); }}
                className="text-xs text-red-400 hover:text-red-600 mt-1">Delete</button>
            </div>
          ))}
        </div>

        {/* Right panel - results */}
        <div className="flex-1 overflow-auto">
          {!detail && (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              Select a competition to view results
            </div>
          )}
          {detail && (
            <div>
              <div className="bg-white border-b border-gray-200 px-6 py-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-green-800">{detail.event.name}</h2>
                  <div className="flex gap-2">
                    <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
                    <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📄 Export PDF</button>
                  </div>
                </div>
                {detail.event.description && <p className="text-sm text-gray-500">{detail.event.description}</p>}
                <div className="flex gap-4 mt-1 flex-wrap">
                  {detail.days.map(d => (
                    <span key={d.day_number} className="text-xs bg-green-50 border border-green-200 rounded px-2 py-0.5 text-green-700">
                      Day {d.day_number}: {d.date ? new Date(d.date).toLocaleDateString('en-GB') : ''} — {d.venue || d.course_name || d.game_type}
                    </span>
                  ))}
                </div>
                <div className="flex gap-4 mt-2 flex-wrap">
                  {detail.days.map(d => (
                    (d.nearest_pin || d.nearest_pin2 || d.longest_drive) && (
                      <span key={d.day_number} className="text-xs bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-yellow-800">
                        <strong>Day {d.day_number} Prizes:</strong>
                        {d.nearest_pin && <> &nbsp;Nearest Pin: <strong>{d.nearest_pin}</strong></>}
                        {d.nearest_pin2 && <> &nbsp;| Nearest Pin 2: <strong>{d.nearest_pin2}</strong></>}
                        {d.longest_drive && <> &nbsp;| Longest Drive: <strong>{d.longest_drive}</strong></>}
                      </span>
                    )
                  ))}
                </div>
              </div>

              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-green-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-center w-12">Rank</th>
                    <th className="px-4 py-2 text-left">Player</th>
                    <th className="px-4 py-2 text-center w-16">HCP</th>
                    {detail.days.map(d => (
                      <th key={d.day_number} className="px-4 py-2 text-center w-20">
                        Day {d.day_number} Gross
                      </th>
                    ))}
                    {detail.days.map(d => (
                      <th key={d.day_number} className="px-4 py-2 text-center w-20">
                        Day {d.day_number}<br/>
                        <span className="font-normal text-xs text-green-200">{isMedal ? "Net" : "Pts"}</span>
                      </th>
                    ))}
                    <th className="px-4 py-2 text-center w-20 bg-green-900">Total {isMedal ? "Net" : "Pts"}</th>
                    <th className="px-4 py-2 text-center w-20 bg-green-900">Total Gross</th>
                    <th className="px-4 py-2 text-center w-16">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.results.map((p, i) => (
                    <tr key={p.player_name} className={"border-b border-gray-200 " + (i % 2 === 0 ? "bg-white" : "bg-green-50")}>
                      <td className="px-4 py-2 text-center font-bold">{p.overall_rank}</td>
                      <td className="px-4 py-2 font-medium">{p.player_name}</td>
                      <td className="px-4 py-2 text-center text-gray-500">{p.handicap}</td>
                      {detail.days.map(d => (
                        <td key={d.day_number} className="px-4 py-2 text-center text-gray-500">
                          {p.days[d.day_number] ? p.days[d.day_number].gross : "—"}
                        </td>
                      ))}
                      {detail.days.map(d => (
                        <td key={d.day_number} className="px-4 py-2 text-center">
                          {p.days[d.day_number] ? (isMedal ? p.days[d.day_number].net : p.days[d.day_number].points) : "—"}
                        </td>
                      ))}
                      <td className="px-4 py-2 text-center font-bold text-blue-700 bg-gray-50">{isMedal ? p.total_net : p.total_points}</td>
                      <td className="px-4 py-2 text-center font-bold bg-gray-50">{p.total_gross}</td>
                      <td className="px-4 py-2 text-center text-gray-500">{p.days_played}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
